/* eslint-disable no-console */
import { MutableRefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classnames from 'classnames';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Hidden,
  Portal,
  Stack,
  Theme,
  Typography,
  useMediaQuery,
} from '@mui/material';
import NetworkCheckIcon from '@mui/icons-material/NetworkCheck';
import { useNavigate, useParams } from 'react-router-dom';
import uuid from 'react-uuid';
import { useTranslation } from 'react-i18next';

import incomingLongSound from 'assets/audio/call/start-call-long-incoming.mp3';
import orbitalLongSound from 'assets/audio/call/start-call-long-orbital.mp3';
import stepsLongSound from 'assets/audio/call/start-call-long-steps.mp3';
import chakraShortSound from 'assets/audio/call/start-call-short-chakra.mp3';
import chocolateShortSound from 'assets/audio/call/start-call-short-chocolate.mp3';
import tadaShortSound from 'assets/audio/call/start-call-short-tada.mp3';

const CALL_SOUND_FILES: Record<string, string> = {
  'incoming-long': incomingLongSound,
  'orbital-long': orbitalLongSound,
  'steps-long': stepsLongSound,
  'chakra-short': chakraShortSound,
  'chocolate-short': chocolateShortSound,
  'tada-short': tadaShortSound,
};
import { DEFAULT_WELCOME_MESSAGE, DEFAULT_UNAVAILABLE_MESSAGE } from 'constants/data';
import { useAppDispatch, useAppSelector } from 'store';
import {
  dismiss,
  selectPatient,
  clearPatientStore,
  selectPatientIsDismissed,
  setPatient as setPatientInStore,
} from 'store/patient';
import { ApiWaitingRoomLinkProfile, Patient } from 'types';
import { CircleCrossIcon, EmailIcon, HeavyCheckIcon, HospitalIcon, UserIcon } from 'assets';
import {
  useAzureAsset,
  useChat,
  useDialog,
  useLocalStorageState,
  useSignalrConnection,
  useTeleconsent,
  useVideo,
} from 'hooks';
import {
  CallStartOverlay,
  MediaContainer,
  SettingsDialog,
  VideoPreviewWithControls,
  InfoDialog,
  TwoOptionsDialog,
} from 'components';
import { ConnectionTestDialog } from 'components/ConnectionTest/ConnectionTestDialog';
import { getDeviceInfo, isPermissionDenied, noop, replaceURLsInString, stringifyIdentity } from 'utils';
import { WelcomeDialog } from '../WelcomeDialog';
import { CheckedInDialog } from '../CheckedInDialog';
import { SessionEnded, SessionEndReason } from '../SessionEnded/SessionEnded';
import {
  getWaitingRoomProfile,
  updateIsProviderInQueueStatusInWaitingRoom,
  updateUnavailableProviderStatusInWaitingRoom,
} from 'store/waitingRoom';
import { isHipaaLink } from '../../../../config/productProfile';

import styles from './PatientWaitingRoom.module.scss';

type PatientConnectionStage = 'none' | 'patientDataAdded' | 'settingsCofigured' | 'checkFormClosed';

type PatientWaitingRoomProps = {
  profile: ApiWaitingRoomLinkProfile;
};

export function PatientWaitingRoom({ profile }: PatientWaitingRoomProps) {
  const { t, i18n } = useTranslation('link');
  const dispatch = useAppDispatch();
  const isRtl =
    i18n.resolvedLanguage?.toLowerCase().startsWith('ar') ||
    i18n.language?.toLowerCase().startsWith('ar') ||
    (typeof document !== 'undefined' && document.documentElement.dir === 'rtl');
  const patientInStore = useAppSelector(selectPatient);
  const isDismissed = useAppSelector(selectPatientIsDismissed);
  const navigate = useNavigate();
  const { link } = useParams<{ link: string }>();
  const {
    updateCallData,
    events,
    signalrConnectionId,
    getToken,
    getIsProviderConnectedToQueue,
    disconnect,
    addEventListener,
    removeEventListener,
  } = useSignalrConnection();

  const {
    connect: videoConnect,
    isAcquiringLocalTracks,
    localTracks,
    room,
    removeLocalVideoTrack,
    removeLocalAudioTrack,
    setIsTransferring,
  } = useVideo();
  const { showDialog } = useDialog();
  const { isOpen: isTeleconsentOpen } = useTeleconsent();
  const avatarUrl = useAzureAsset(profile.linkToProfilePicture);
  const { identity, conversation, openChat } = useChat();
  const isTablet = useMediaQuery((theme: Theme) => theme.breakpoints.down('md') && theme.breakpoints.up('sm'));
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

  const [previousPatientDataToReconnect, setPreviousPatientDataToReconnect] = useLocalStorageState<Patient | null>(
    'previousPatientDataToReconnect',
  );
  const [patient, setPatient] = useState<Patient | null>(null);
  const [patientConnectionStage, setPatientConnectionStage] = useState<PatientConnectionStage>('none');
  const [callEndReason, setCallEndReason] = useState<SessionEndReason | null>(null);
  const [connectingMessage, setConnectingMessage] = useState<string | null>(null);
  const [connectionTestOpen, setConnectionTestOpen] = useState(false);

  const isDebugMode = new URLSearchParams(window.location.search).has('debug');
  const [debugLogs, setDebugLogs] = useState<string[]>([]);
  // Ref so effects with [] deps always call the latest version without needing it as a dep.
  const addLog = useRef<(msg: string) => void>(console.log.bind(console));
  addLog.current = (msg: string) => {
    console.log(msg);
    if (isDebugMode) {
      setDebugLogs((prev) => [...prev.slice(-49), `${new Date().toLocaleTimeString()} ${msg}`]);
    }
  };

  useEffect(() => {
    patientRef.current = patient;
  }, [patient]);

  // Once the new SignalR connection is established after a transfer, finalize the
  // transfer: remove the overlay, reset guard flags, and cancel the fallback timer.
  useEffect(() => {
    if (signalrConnectionId) {
      setIsTransferring(false);

      if (isTransferInProgress.current) {
        isTransferInProgress.current = false;
        doNotHandleDisconnectionFromSignalr.current = false;

        if (transferFallbackTimerRef.current !== null) {
          clearTimeout(transferFallbackTimerRef.current);
          transferFallbackTimerRef.current = null;
        }

        const overlay = document.getElementById('transfer-overlay');
        if (overlay) overlay.remove();
      }
    }
  }, [signalrConnectionId]);
  const patientNameForGreeting = patient?.userName || '';
  const detailsIconSlotSx = {
    width: 22,
    minWidth: 22,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  } as const;
  const mobileStatusIconSlotSx = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: 0,
  } as const;
  const greetingText = useMemo(() => {
    if (!patientNameForGreeting) {
      return '';
    }

    const localizedGreeting = t('patientWaitingRoom.hiUser', { name: patientNameForGreeting });
    return isRtl ? `\u200F${localizedGreeting}` : localizedGreeting;
  }, [isRtl, patientNameForGreeting, t]);
  const doNotHandleDisconnectionFromSignalr: MutableRefObject<boolean> = useRef<boolean>(false);
  const localTracksStartedAcquiring: MutableRefObject<boolean> = useRef<boolean>(false);
  const activeDialogRef = useRef<{ destroy: () => void } | null>(null);
  const patientRef = useRef<Patient | null>(null);
  const isTransferInProgress = useRef<boolean>(false);
  const isRequeueing = useRef<boolean>(false);
  const transferFallbackTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingTransferUrlRef = useRef<string | null>(null);
  const callAudioRef = useRef<HTMLAudioElement | null>(null);
  const callAudioFadeRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const callSourceRef = useRef<AudioBufferSourceNode | null>(null);
  const callGainRef = useRef<GainNode | null>(null);

  const pendingJoinRef = useRef<{
    roomId: string;
    providerId: string;
  } | null>(null);

  // iOS blocks audio.play() without a prior user gesture. HTMLAudioElement.play()
  // does not reliably carry that permission into later async/SignalR callbacks on
  // WKWebView (iOS Chrome). AudioContext does: once ctx.resume() is called inside
  // a gesture, the context stays 'running' for the page session and can play audio
  // from any async context. A single `unlocked` flag prevents the double-fire that
  // occurs because both touchstart and pointerdown fire for the same touch.
  useEffect(() => {
    let unlocked = false;
    const unlock = () => {
      if (unlocked) return;
      unlocked = true;
      addLog.current('[SOUND] user gesture — creating AudioContext');
      try {
        const Ctx = (window.AudioContext ||
          (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext) as
          | typeof AudioContext
          | undefined;
        if (!Ctx) {
          addLog.current('[SOUND] AudioContext not supported — will fall back to HTMLAudioElement');
          return;
        }
        const ctx = new Ctx();
        audioCtxRef.current = ctx;
        ctx
          .resume()
          .then(() => {
            // Play a 1-sample silent buffer to confirm the context is truly running.
            const buf = ctx.createBuffer(1, 1, 22050);
            const src = ctx.createBufferSource();
            src.buffer = buf;
            src.connect(ctx.destination);
            src.start(0);
            addLog.current(`[SOUND] AudioContext unlocked — state: ${ctx.state}`);
          })
          .catch((err) => addLog.current(`[SOUND] AudioContext resume failed: ${err}`));
      } catch (err) {
        addLog.current(`[SOUND] AudioContext creation failed: ${err}`);
      }
    };
    document.addEventListener('touchstart', unlock, { passive: true, once: true });
    document.addEventListener('pointerdown', unlock, { once: true });
    return () => {
      document.removeEventListener('touchstart', unlock);
      document.removeEventListener('pointerdown', unlock);
    };
  }, []);

  // iOS virtual keyboard fix — when the on-screen keyboard is dismissed after
  // typing in the check-in dialog, iOS leaves the page scrolled up. Detect the
  // viewport growing (keyboard closed) and snap the scroll back to the origin.
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    let prevHeight = vv.height;
    const onResize = () => {
      const curr = vv.height;
      if (curr > prevHeight + 100) {
        window.scrollTo(0, 0);
      }
      prevHeight = curr;
    };
    vv.addEventListener('resize', onResize);
    return () => vv.removeEventListener('resize', onResize);
  }, []);

  const profileFullName = useMemo(() => {
    let fullName = profile.preferedDisplayName;
    if (!fullName) {
      fullName = [profile.title, profile.firstName, profile.lastName].filter((t) => t && t).join(' ');

      if (isHipaaLink && profile.suffix) {
        fullName += `, ${profile.suffix}`;
      }
    }

    return fullName;
  }, [profile]);

  const appTitle = useMemo(() => {
    const fromEnv = (process.env.REACT_APP_BRAND_NAME ?? '').trim();
    return fromEnv || 'VIA3';
  }, []);

  const hostNameForTitle = useMemo(() => {
    const fallback = [profile.firstName, profile.lastName].filter(Boolean).join(' ').trim();
    return (profileFullName || fallback || 'Host').trim();
  }, [profile.firstName, profile.lastName, profileFullName]);

  const unavailableMessage = useMemo(() => {
    //eslint-disable-next-line
    return Boolean(profile.unavailableMessage) ? profile.unavailableMessage : t(DEFAULT_UNAVAILABLE_MESSAGE);
  }, [profile, t]);

  const welcomeMessage = useMemo(() => {
    //eslint-disable-next-line
    return Boolean(profile.welcomeMessage) ? profile.welcomeMessage : t(DEFAULT_WELCOME_MESSAGE);
  }, [profile, t]);

  useEffect(() => {
    return () => {
      if (activeDialogRef.current) {
        activeDialogRef.current.destroy();
        activeDialogRef.current = null;
      }
      if (callAudioFadeRef.current) {
        clearInterval(callAudioFadeRef.current);
        callAudioFadeRef.current = null;
      }
      if (callAudioRef.current) {
        callAudioRef.current.pause();
        callAudioRef.current = null;
      }
      if (callSourceRef.current) {
        try {
          callSourceRef.current.stop();
        } catch {
          // already stopped
        }
        callSourceRef.current = null;
      }
      callGainRef.current = null;
    };
  }, []);

  useEffect(() => {
    const defaultTitle = t('tabTitle.default', { appTitle });
    let nextTitle = defaultTitle;

    if (room) {
      nextTitle = t('tabTitle.secureSessionWithHost', { appTitle, hostName: hostNameForTitle });
    } else if (patientConnectionStage === 'none') {
      nextTitle = t('tabTitle.checkInWithHost', { hostName: hostNameForTitle, appTitle });
    } else if (patientConnectionStage === 'patientDataAdded') {
      nextTitle = t('tabTitle.deviceCheck', { appTitle });
    } else if (patientConnectionStage === 'settingsCofigured' || patientConnectionStage === 'checkFormClosed') {
      nextTitle = t('tabTitle.hostWaitingRoom', { hostName: hostNameForTitle, appTitle });
    }

    document.title = nextTitle;

    return () => {
      document.title = defaultTitle;
    };
  }, [appTitle, hostNameForTitle, patientConnectionStage, room, t]);

  function showTransferOverlay() {
    const overlay = document.createElement('div');
    overlay.id = 'transfer-overlay';
    overlay.style.position = 'fixed';
    overlay.style.top = '0';
    overlay.style.left = '0';
    overlay.style.width = '100vw';
    overlay.style.height = '100vh';
    overlay.style.zIndex = '9999';
    overlay.style.background = 'var(--app-overlay-scrim)';
    overlay.style.display = 'flex';
    overlay.style.flexDirection = 'column';
    overlay.style.justifyContent = 'center';
    overlay.style.alignItems = 'center';

    const spinner = document.createElement('div');
    spinner.style.border = '6px solid #eee';
    spinner.style.borderTop = '6px solid var(--app-brand-primary)';
    spinner.style.borderRadius = '50%';
    spinner.style.width = '48px';
    spinner.style.height = '48px';
    spinner.style.animation = 'spin 1s linear infinite';
    spinner.style.marginBottom = '2rem';

    const message = document.createElement('p');
    message.textContent = t('patientWaitingRoom.transferringOverlayMessage');
    message.style.fontSize = '1.6rem';
    message.style.color = 'var(--app-text-default)';
    message.style.textAlign = 'center';
    message.style.fontWeight = '500';
    message.style.maxWidth = '90%';

    overlay.appendChild(spinner);
    overlay.appendChild(message);
    document.body.appendChild(overlay);
  }

  ////////////////////////////////////////////////
  /// Events listening
  ////////////////////////////////////////////////

  const onKicked = useCallback(() => {
    if (isRequeueing.current) return;
    doNotHandleDisconnectionFromSignalr.current = true;
    dispatch(dismiss());
    dispatch(clearPatientStore());
    disconnect();
    removeLocalAudioTrack();
    removeLocalVideoTrack();
    room?.disconnect();
    setCallEndReason('removed');
  }, [room, removeLocalAudioTrack, removeLocalVideoTrack]);

  const onCallEnded = useCallback(() => {
    if (isRequeueing.current) return;
    doNotHandleDisconnectionFromSignalr.current = true;
    dispatch(dismiss());
    dispatch(clearPatientStore());

    disconnect();

    if (room && room.localParticipant) {
      room.localParticipant.audioTracks.forEach(({ track }) => {
        track.stop();
        room.localParticipant.unpublishTrack(track);
      });

      room.localParticipant.videoTracks.forEach(({ track }) => {
        track.stop();
        room.localParticipant.unpublishTrack(track);
      });
    }

    removeLocalAudioTrack();
    removeLocalVideoTrack();
    room?.disconnect();
    setCallEndReason('ended');
  }, [room, removeLocalAudioTrack, removeLocalVideoTrack, navigate]);

  const onDisconnected = useCallback(() => {
    if (doNotHandleDisconnectionFromSignalr.current) {
      return;
    }

    dispatch(dismiss());
    dispatch(clearPatientStore());
    disconnect();

    removeLocalAudioTrack();
    removeLocalVideoTrack();

    room?.disconnect();

    const modal = showDialog(InfoDialog, {
      title: t(
        isHipaaLink
          ? 'patientWaitingRoom.removedFromWaitingRoomTitle'
          : 'patientWaitingRoom.removedFromWaitingRoomTitleVia3',
      ),
      onConfirm: () => {
        modal.destroy();
        navigate('/login');
      },
    });
  }, [room, patient, removeLocalAudioTrack, removeLocalVideoTrack]);

  const onReconnected = useCallback(() => {
    if (doNotHandleDisconnectionFromSignalr.current) {
      return;
    }

    dispatch(dismiss());
    dispatch(clearPatientStore());

    removeLocalAudioTrack();
    removeLocalVideoTrack();

    room?.disconnect();

    const modal = showDialog(TwoOptionsDialog, {
      title: t('patientWaitingRoom.disconnectedTitle'),
      text: t('patientWaitingRoom.disconnectedText'),
      confirmText: t('patientWaitingRoom.reconnect'),
      onConfirm: () => {
        setPreviousPatientDataToReconnect({ ...patient!, id: uuid() });
        modal.destroy();
        window.location.reload();
      },
    });
  }, [room, patient, removeLocalAudioTrack, removeLocalVideoTrack]);

  useEffect(() => {
    if (!signalrConnectionId) {
      console.warn('⏳ Waiting for SignalR connection...');
      return;
    }

    let retryAttempts = 0; // ✅ Retry counter
    const maxRetries = 5; // ✅ Max retry attempts
    const retryInterval = 500; // ✅ Retry every 500ms

    const checkSignalRConnection = () => {
      if (!window.signalrConnection || !window.signalrConnection.current) {
        retryAttempts++;

        if (retryAttempts > maxRetries) {
          console.error(`❌ SignalR connection failed after ${maxRetries} attempts. Giving up.`);
          return;
        }

        console.warn(
          `⚠️ SignalR connection not initialized. Retrying in ${retryInterval}ms... (Attempt ${retryAttempts}/${maxRetries})`,
        );

        setTimeout(checkSignalRConnection, retryInterval);
        return;
      }

      console.log('✅ SignalR connection established.');

      // ✅ Always bind "Transferred" listener immediately when connected
      window.signalrConnection.current.off('Transferred', handleTransferred);
      window.signalrConnection.current.on('Transferred', handleTransferred);

      // ✅ Continue with remaining logic only after check-in stage
      if (patientConnectionStage !== 'checkFormClosed') {
        console.warn('⏳ Patient has not completed check-in. Waiting to proceed with SignalR-dependent logic...');
        return;
      }

      // ✅ Any extra setup that needs patientConnectionStage === 'checkFormClosed' would go here
    };

    checkSignalRConnection();

    return () => {
      console.log('❌ Removing SignalR listener for "Transferred" event...');
      if (window.signalrConnection?.current) {
        window.signalrConnection.current.off('Transferred', handleTransferred);
      }
    };
  }, [
    signalrConnectionId,
    patientConnectionStage,
    dispatch,
    removeLocalAudioTrack,
    removeLocalVideoTrack,
    room,
    disconnect,
  ]);

  const handleTransferred = (newRoomUrlOrEvent?: string | CustomEvent) => {
    console.log('📡 Transferred event fired BEFORE callEnded');
    let newRoomUrl: string | undefined;

    if (typeof newRoomUrlOrEvent === 'string') {
      newRoomUrl = newRoomUrlOrEvent;
    } else if (newRoomUrlOrEvent?.detail) {
      newRoomUrl = newRoomUrlOrEvent.detail;
    }

    if (!newRoomUrl) {
      console.warn('⚠️ Transfer event triggered, but no valid newRoomUrl provided!');
      return;
    }

    console.log('✅ Transfer event received. Redirecting to:', newRoomUrl);

    const runTransfer = async () => {
      if (!patientRef.current) {
        console.warn('⚠️ No patient data found before transfer.');
        return;
      }

      const newPath = new URL(newRoomUrl as string).pathname;
      const newLink = newPath.replace(/^\/+/, '');

      showTransferOverlay();

      // Real transfer to a different provider's queue.

      // Prevent the SignalR onclose handler from treating this as an error disconnect
      doNotHandleDisconnectionFromSignalr.current = true;

      // Mark transfer in progress — overlay and flag cleanup happen in the
      // signalrConnectionId useEffect once the new connection is confirmed.
      isTransferInProgress.current = true;

      // Tell VideoProvider not to remove local tracks when signalrConnectionId → ''
      setIsTransferring(true);

      // Leave the old provider's SignalR queue.
      // SignalrConnectionProvider watches waitingRoomProfile?.providerId — when
      // getWaitingRoomProfile loads the new profile below, it will automatically
      // build a new HubConnection to the new provider's queue.
      disconnect();

      // If somehow already in an active Twilio room call, leave it.
      // room.disconnect() exits the room but does NOT stop local MediaStream tracks.
      if (room) {
        room.disconnect();
      }

      // Do NOT call removeLocalAudioTrack() or removeLocalVideoTrack().
      // The MediaStream (camera/mic) is independent of the Twilio room connection.
      // Keeping tracks alive means no device re-acquisition on the new queue.

      // Update the browser URL to reflect the new room without a page reload.
      window.history.replaceState({}, '', newRoomUrl as string);

      // Extract the room slug (e.g. "doctor" from "https://app.com/doctor") and
      // load the new provider's profile into Redux. SignalrConnectionProvider's
      // useEffect depends on waitingRoomProfile?.providerId and will reconnect.
      const result = await dispatch(getWaitingRoomProfile(newLink));

      if (result.payload === null) {
        console.error('❌ Transfer failed: new room profile not found for', newLink);
        isTransferInProgress.current = false;
        doNotHandleDisconnectionFromSignalr.current = false;
        const overlay = document.getElementById('transfer-overlay');
        if (overlay) overlay.remove();
        showDialog(InfoDialog, {
          title: t('patientWaitingRoom.transferFailedTitle'),
          onConfirm: () => window.location.reload(),
        });
        return;
      }

      // Reset stage so the CheckedIn dialog re-fires once the new signalrConnectionId
      // arrives (it won't show while signalrConnectionId is empty after disconnect).
      setPatientConnectionStage('settingsCofigured');

      // Fallback: if SignalR never reconnects within 10 seconds, remove the overlay
      // and show an error rather than leaving the patient stuck behind a spinner.
      transferFallbackTimerRef.current = setTimeout(() => {
        if (isTransferInProgress.current) {
          console.error('❌ Transfer fallback: SignalR did not reconnect within 10 seconds');
          isTransferInProgress.current = false;
          doNotHandleDisconnectionFromSignalr.current = false;
          const overlay = document.getElementById('transfer-overlay');
          if (overlay) overlay.remove();
          showDialog(InfoDialog, {
            title: t('patientWaitingRoom.transferFailedTitle'),
            onConfirm: () => window.location.reload(),
          });
        }
      }, 10000);
    };

    if (patientConnectionStage !== 'checkFormClosed') {
      // Auto-dismiss the CheckedIn dialog so the transfer isn't blocked by user interaction.
      if (activeDialogRef.current) {
        activeDialogRef.current.destroy();
        activeDialogRef.current = null;
      }
      pendingTransferUrlRef.current = newRoomUrl;
      setPatientConnectionStage('checkFormClosed');
    } else {
      runTransfer();
    }
  };

  // When the patient confirms the CheckedInDialog and the stage advances to
  // 'checkFormClosed', run any transfer that was deferred while the dialog was open.
  useEffect(() => {
    if (patientConnectionStage === 'checkFormClosed' && pendingTransferUrlRef.current) {
      const url = pendingTransferUrlRef.current;
      pendingTransferUrlRef.current = null;
      handleTransferred(url);
    }
  }, [patientConnectionStage]);

  // If a teleconsent session launches while the CheckedInDialog is still open,
  // close it immediately so the consent form is not blocked.
  useEffect(() => {
    if (!isTeleconsentOpen) return;
    if (activeDialogRef.current) {
      activeDialogRef.current.destroy();
      activeDialogRef.current = null;
    }
    if (patientConnectionStage === 'settingsCofigured') {
      setPatientConnectionStage('checkFormClosed');
    }
  }, [isTeleconsentOpen]);

  const onRequeued = useCallback(() => {
    isRequeueing.current = true;
    showTransferOverlay();
    room?.disconnect();
    setTimeout(() => {
      isRequeueing.current = false;
      const overlay = document.getElementById('transfer-overlay');
      if (overlay) overlay.remove();
      getIsProviderConnectedToQueue();
    }, 1500);
  }, [room, getIsProviderConnectedToQueue]);

  useEffect(() => {
    addEventListener('removedFromCall', onKicked);
    addEventListener('callEnded', onCallEnded);
    addEventListener('disconnected', onDisconnected);
    addEventListener('reconnected', onReconnected);
    addEventListener('requeued', onRequeued);

    return () => {
      removeEventListener('removedFromCall', onKicked);
      removeEventListener('callEnded', onCallEnded);
      removeEventListener('disconnected', onDisconnected);
      removeEventListener('reconnected', onReconnected);
      removeEventListener('requeued', onRequeued);
    };
  }, [onKicked, onCallEnded, onDisconnected, onReconnected, onRequeued]);

  const onStartCall = useCallback(
    (roomId: string, providerId: string) => {
      console.warn('[PATIENT][JOIN_CALL][RECEIVED]', {
        roomId,
        providerId,
        hasPatient: !!patientInStore,
        stage: patientConnectionStage,
      });

      setConnectingMessage(t('callOverlay.starting', { name: hostNameForTitle }));

      // Persist intent
      pendingJoinRef.current = { roomId, providerId };

      tryJoinCall();
    },
    [patientInStore, patientConnectionStage, hostNameForTitle, t],
  );

  const tryJoinCall = useCallback(() => {
    if (!pendingJoinRef.current) {
      return;
    }

    if (!patientInStore) {
      console.warn('[PATIENT][JOIN_CALL][WAIT] patientInStore missing');
      return;
    }

    if (patientConnectionStage !== 'checkFormClosed') {
      if (patientConnectionStage === 'settingsCofigured') {
        // If provider starts the call while check-in dialog is still open, advance and continue.
        if (activeDialogRef.current) {
          activeDialogRef.current.destroy();
          activeDialogRef.current = null;
        }

        setPatientConnectionStage('checkFormClosed');
      }

      console.warn('[PATIENT][JOIN_CALL][WAIT] stage=', patientConnectionStage);
      return;
    }

    if (isAcquiringLocalTracks) {
      console.warn('[PATIENT][JOIN_CALL][WAIT] acquiring local tracks');
      return;
    }

    const { roomId, providerId } = pendingJoinRef.current;
    pendingJoinRef.current = null;

    const identity = stringifyIdentity([providerId, patientInStore.userName]);

    console.warn('[PATIENT][JOIN_CALL][CONNECT]', {
      roomId,
      identity,
    });

    getToken({ identity, roomId })
      .then((token) => {
        console.warn('[PATIENT][JOIN_CALL][TOKEN_OK]', token.length);
        videoConnect(token);
      })
      .catch((err) => {
        console.error('[PATIENT][JOIN_CALL][FAILED]', err);
      });
  }, [patientInStore, patientConnectionStage, isAcquiringLocalTracks, getToken, videoConnect]);

  useEffect(() => {
    tryJoinCall();
  }, [tryJoinCall, patientInStore, patientConnectionStage, isAcquiringLocalTracks]);

  // Once the Twilio room is established, resolve the overlay text, stop the ring tone, and dismiss
  useEffect(() => {
    if (!room) return;

    // Fade out AudioContext source via GainNode ramp.
    if (callSourceRef.current && callGainRef.current && audioCtxRef.current) {
      const ctx = audioCtxRef.current;
      const gain = callGainRef.current;
      const source = callSourceRef.current;
      callSourceRef.current = null;
      callGainRef.current = null;
      gain.gain.setValueAtTime(gain.gain.value, ctx.currentTime);
      gain.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.5);
      setTimeout(() => {
        try {
          source.stop();
        } catch {
          // already stopped
        }
      }, 550);
    }

    // Fade out HTMLAudioElement (fallback path).
    if (callAudioRef.current) {
      const audio = callAudioRef.current;
      callAudioRef.current = null;
      const step = 25;
      const decrement = audio.volume / (500 / step);
      callAudioFadeRef.current = setInterval(() => {
        if (audio.volume > decrement) {
          audio.volume = Math.max(0, audio.volume - decrement);
        } else {
          audio.volume = 0;
          audio.pause();
          audio.currentTime = 0;
          clearInterval(callAudioFadeRef.current!);
          callAudioFadeRef.current = null;
        }
      }, step);
    }

    if (!connectingMessage) return;

    // ≥2 remote participants means host + at least one other patient → joining existing session
    const isJoining = room.participants.size >= 2;
    if (isJoining) {
      setConnectingMessage(t('callOverlay.joining', { name: hostNameForTitle }));
    }

    const timer = setTimeout(() => setConnectingMessage(null), 600);
    return () => clearTimeout(timer);
  }, [room]);

  useEffect(() => {
    const handleCallPreparing = async () => {
      setConnectingMessage(t('callOverlay.starting', { name: hostNameForTitle }));
      let soundKey = profile.startCallSound;
      try {
        const fresh = await dispatch(getWaitingRoomProfile(link!)).unwrap();
        if (fresh) soundKey = fresh.startCallSound;
      } catch {
        // fall back to the value loaded at page open
      }
      const soundFile = CALL_SOUND_FILES[soundKey];
      if (soundFile) {
        const ctx = audioCtxRef.current;
        if (ctx && !callSourceRef.current) {
          // AudioContext path — works in async/SignalR callbacks on iOS WKWebView.
          // iOS auto-suspends the context during inactivity; resume() is allowed
          // without a gesture after the first user-gesture unlock.
          addLog.current(`[SOUND] ctx state before resume: ${ctx.state}`);
          ctx
            .resume()
            .then(() => {
              addLog.current(`[SOUND] ctx state after resume: ${ctx.state} — fetching "${soundKey}"`);
              return fetch(soundFile)
                .then((r) => r.arrayBuffer())
                .then((buf) => ctx.decodeAudioData(buf));
            })
            .then((decoded) => {
              if (callSourceRef.current) return;
              const gain = ctx.createGain();
              gain.gain.setValueAtTime(1, ctx.currentTime);
              gain.connect(ctx.destination);
              const source = ctx.createBufferSource();
              source.buffer = decoded;
              source.loop = !soundKey.endsWith('-short');
              source.connect(gain);
              source.start(0);
              callSourceRef.current = source;
              callGainRef.current = gain;
              addLog.current('[SOUND] AudioContext play started ✓');
            })
            .catch((err) => addLog.current(`[SOUND] AudioContext failed: ${err}`));
        } else if (!ctx && !callAudioRef.current) {
          // True fallback: AudioContext not available (very old browser, non-iOS desktop).
          addLog.current(`[SOUND] HTMLAudioElement fallback (no AudioContext): key="${soundKey}"`);
          const audio = new Audio(soundFile);
          audio.loop = !soundKey.endsWith('-short');
          audio
            .play()
            .then(() => addLog.current('[SOUND] HTMLAudioElement play() succeeded'))
            .catch((err) => addLog.current(`[SOUND] HTMLAudioElement play() FAILED: ${err}`));
          callAudioRef.current = audio;
        }
      }
    };

    const handleJoinCallDebug = (...args: any[]) => {
      console.warn('[PATIENT][JOIN_CALL][RAW_EVENT]', {
        args,
        argCount: args.length,
        arg0: args[0],
        arg1: args[1],
        arg0Type: typeof args[0],
        arg1Type: typeof args[1],
      });

      // Preserve existing behavior
      // Expecting: (roomId: string, providerId: string)
      onStartCall(args[0], args[1]);
    };

    events.on('callPreparing', handleCallPreparing);
    events.on('joinCall', handleJoinCallDebug);

    events.on('updatedUnavailableProviderStatus', (unavailable: boolean) =>
      dispatch(updateUnavailableProviderStatusInWaitingRoom(unavailable)),
    );

    events.on('isProviderInQueueSent', (isProviderInQueue: boolean) =>
      dispatch(updateIsProviderInQueueStatusInWaitingRoom(isProviderInQueue)),
    );

    return () => {
      events.off('callPreparing', handleCallPreparing);
      events.off('joinCall', handleJoinCallDebug);
    };
  }, [events, onStartCall, hostNameForTitle, t]);

  ////////////////////////////////////////////////
  /// Preparing patinet to connection
  ////////////////////////////////////////////////

  const updatePatientData = async () => {
    const hasVideoTrack = localTracks.some((t) => t.kind === 'video');
    const hasAudioTrack = localTracks.some((t) => t.kind === 'audio');

    const { hasAudioInputDevices, hasVideoInputDevices } = await getDeviceInfo();

    const getCameraError = async (): Promise<string | undefined> => {
      if (hasVideoTrack) return undefined;
      if (!hasVideoInputDevices) return 'noDevice';
      if (await isPermissionDenied('camera')) return 'permissionDenied';
      return 'notReadable'; // device present, permission granted, but track unavailable (OS block / in use)
    };

    const getMicError = async (): Promise<string | undefined> => {
      if (hasAudioTrack) return undefined;
      if (!hasAudioInputDevices) return 'noDevice';
      if (await isPermissionDenied('microphone')) return 'permissionDenied';
      return 'notReadable';
    };

    const [cameraAccessError, microAccessError] = await Promise.all([getCameraError(), getMicError()]);

    updateCallData({
      callAccesses: {
        hasCameraAccess: !cameraAccessError,
        hasMicroAccess: !microAccessError,
        cameraAccessError,
        microAccessError,
      },
    });
  };

  useEffect(() => {
    if (signalrConnectionId && patientConnectionStage === 'settingsCofigured') {
      updatePatientData().catch(noop);
      getIsProviderConnectedToQueue();

      activeDialogRef.current?.destroy();
      const modal = showDialog(CheckedInDialog, {
        onConfirm: () => {
          modal.destroy();
          setPatientConnectionStage('checkFormClosed');
        },
      });
      activeDialogRef.current = modal;
    }
  }, [patient, signalrConnectionId, patientConnectionStage]);

  useEffect(() => {
    if (
      patientConnectionStage === 'patientDataAdded' &&
      !isAcquiringLocalTracks &&
      localTracksStartedAcquiring.current
    ) {
      activeDialogRef.current?.destroy();
      const modal = showDialog(SettingsDialog, {
        onConfirm: async () => {
          dispatch(setPatientInStore(patient!));
          setPatientConnectionStage('settingsCofigured');
          modal.update({ isLoading: true });
        },
        onClose: () => {
          dispatch(setPatientInStore(patient!));
          setPatientConnectionStage('settingsCofigured');
        },
      });
      activeDialogRef.current = modal;
    }

    if (!localTracksStartedAcquiring.current && isAcquiringLocalTracks) {
      localTracksStartedAcquiring.current = true;
    }
  }, [patientConnectionStage, isAcquiringLocalTracks]);

  useEffect(() => {
    if (patientConnectionStage === 'none') {
      if (previousPatientDataToReconnect) {
        console.log('🔄 Reconnecting previous patient session...');
        setPatient(previousPatientDataToReconnect);
        dispatch(setPatientInStore(previousPatientDataToReconnect));
        setPreviousPatientDataToReconnect(null);
        setPatientConnectionStage('settingsCofigured');
        return;
      }

      console.log('📡 New patient session detected. Showing WelcomeDialog...');
      const modal = showDialog(WelcomeDialog, {
        avatarUrl,
        organizationName: profile.organization,
        profileFullName,
        requirePhoneNumber: profile.requirePhoneNumber,
        onConfirm: (patient: Patient) => {
          setPatient(patient);
          setPatientConnectionStage('patientDataAdded');
          modal.update({ isLoading: true });
          setTimeout(() => {
            window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
          }, 300);
        },
      });
      activeDialogRef.current = modal;
    }
  }, [patientConnectionStage, previousPatientDataToReconnect]);

  const handleMessageClick = (): void => {
    if (!conversation && !profile.unavailable && profile.isProviderInQueue) {
      openChat(profile.providerId, identity);
    }
  };

  const isCheckInBlurred = patientConnectionStage === 'none' || patientConnectionStage === 'patientDataAdded';

  const [blurVisible, setBlurVisible] = useState(isCheckInBlurred);
  const [blurActive, setBlurActive] = useState(isCheckInBlurred);

  useEffect(() => {
    if (isCheckInBlurred) {
      setBlurVisible(true);
      setBlurActive(true);
    } else {
      setBlurActive(false);
      const timer = setTimeout(() => setBlurVisible(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [isCheckInBlurred]);

  if (callEndReason) {
    return <SessionEnded reason={callEndReason} profile={profile} avatarUrl={avatarUrl} hostName={hostNameForTitle} />;
  }

  return (
    <>
      {isDebugMode && (
        <Box
          sx={{
            position: 'fixed',
            bottom: 16,
            left: 16,
            right: 16,
            maxHeight: 220,
            overflowY: 'auto',
            bgcolor: 'rgba(0,0,0,0.88)',
            color: '#4eff91',
            p: 1,
            zIndex: 9999,
            borderRadius: 1,
            fontSize: 11,
            fontFamily: 'monospace',
            pointerEvents: 'none',
          }}
        >
          {debugLogs.length === 0 ? (
            <Box component='span' sx={{ color: '#888', fontSize: 11, fontFamily: 'monospace' }}>
              Debug active — waiting for audio events...
            </Box>
          ) : (
            debugLogs.map((entry, i) => <div key={i}>{entry}</div>)
          )}
        </Box>
      )}
      {connectingMessage && <CallStartOverlay message={connectingMessage} />}
      <ConnectionTestDialog open={connectionTestOpen} onClose={() => setConnectionTestOpen(false)} />
      {blurVisible && (
        <Portal>
          <div
            className={classnames(styles.checkInBlurOverlay, {
              [styles.checkInBlurOverlayActive]: blurActive,
            })}
          />
        </Portal>
      )}

      {patient && !isDismissed && <MediaContainer />}

      <Hidden smUp>
        <Box>{!isDismissed && <VideoPreviewWithControls />}</Box>
      </Hidden>

      <Card
        className={classnames(styles.main, {
          [styles.mainTablet]: isTablet,
          [styles.mainMobile]: isMobile,
        })}
      >
        <CardHeader title={t('waitingRoomHeader', { defaultValue: 'WAITING ROOM' })} />
        <CardContent
          className={classnames(styles.content, {
            [styles.contentTablet]: isTablet,
            [styles.contentMobile]: isMobile,
          })}
        >
          <Stack direction='row' spacing={'20px'} alignItems='flex-start' justifyContent='space-between'>
            <Stack direction='column' spacing={'14px'}>
              {patient && !isDismissed && (
                <Typography component='p' variant='h2' sx={{ unicodeBidi: 'plaintext' }}>
                  {greetingText}
                </Typography>
              )}
              {!isDismissed &&
                profile.isProviderInQueue !== undefined &&
                (profile.unavailable || !profile.isProviderInQueue ? (
                  <Typography component='p' variant='body2'>
                    {unavailableMessage}
                  </Typography>
                ) : (
                  <Typography component='p' variant='body2'>
                    {welcomeMessage}
                  </Typography>
                ))}

              <Hidden smDown>
                <Stack direction='row' spacing={'26px'} alignItems='flex-start'>
                  <Stack spacing={'10px'} alignItems='flex-start'>
                    <Avatar
                      className={styles.hostAvatar}
                      sx={{
                        maxWidth: 150,
                        maxHeight: 150,
                        minWidth: 100,
                        minHeight: 100,
                        width: '100%',
                        height: '100%',
                        flex: 1,
                        borderRadius: 3,
                      }}
                      variant='square'
                      src={avatarUrl}
                    >
                      <Box className={styles.avatarFallback}>
                        <UserIcon sx={{ color: 'var(--app-welcome-panel-icon-fg)', fontSize: '5.6rem' }} />
                      </Box>
                    </Avatar>

                    {!isDismissed && (
                      <Button
                        variant='contained'
                        disabled={profile.unavailable || !profile.isProviderInQueue}
                        className={styles.messageBtn}
                        onClick={handleMessageClick}
                      >
                        {t('patientWaitingRoom.message')}
                      </Button>
                    )}
                  </Stack>

                  <Stack direction='column' spacing={'10px'} className={styles.detailsColumn}>
                    <Stack spacing={'7px'} className={styles.detailsHeader}>
                      <Typography
                        component='p'
                        variant='h4'
                        sx={isRtl ? { textAlign: 'right', alignSelf: 'flex-end' } : undefined}
                      >
                        {profileFullName}
                      </Typography>

                      {profile.speciality && (
                        <Typography
                          component='p'
                          variant='body2'
                          color='var(--app-text-muted)'
                          className={styles.detailsValue}
                        >
                          <i>{profile.speciality}</i>
                        </Typography>
                      )}
                    </Stack>

                    {!isDismissed &&
                      profile.isProviderInQueue !== undefined &&
                      (profile.unavailable || !profile.isProviderInQueue ? (
                        <Stack direction='row' spacing={0} alignItems='center' className={styles.detailsRow}>
                          <Box sx={detailsIconSlotSx}>
                            <CircleCrossIcon sx={{ color: 'var(--app-text-muted)', fontSize: '1.6rem' }} />
                          </Box>
                          <Typography component='p' variant='body2' className={styles.detailsValue}>
                            {t('patientWaitingRoom.unavailable')}
                          </Typography>
                        </Stack>
                      ) : (
                        <Stack direction='row' spacing={0} alignItems='center' className={styles.detailsRow}>
                          <Box sx={detailsIconSlotSx}>
                            <HeavyCheckIcon />
                          </Box>
                          <Typography component='p' variant='body2' className={styles.detailsValue}>
                            {t('patientWaitingRoom.available')}
                          </Typography>
                        </Stack>
                      ))}

                    <Stack direction='row' spacing={0} alignItems='center' className={styles.detailsRow}>
                      <Box sx={detailsIconSlotSx}>
                        <HospitalIcon className={styles.hospitalIcon} />
                      </Box>
                      <Typography
                        component='p'
                        variant='body2'
                        className={classnames(styles.detailsValue, styles.detailsValueLatin)}
                      >
                        {profile.organization}
                      </Typography>
                    </Stack>

                    {profile.displayEmail && (
                      <Stack direction='row' spacing={0} alignItems='center' className={styles.detailsRow}>
                        <Box sx={detailsIconSlotSx}>
                          <EmailIcon className={styles.emailIcon} color='primary' />
                        </Box>
                        <Typography
                          component='p'
                          variant='body2'
                          className={classnames(styles.detailsValue, styles.detailsValueLatin)}
                        >
                          {profile.email}
                        </Typography>
                      </Stack>
                    )}
                  </Stack>
                </Stack>
              </Hidden>

              <Hidden smUp>
                <Stack direction='column' spacing={'15px'} alignItems={isRtl ? 'stretch' : 'flex-start'}>
                  <Stack direction='row' spacing={'15px'}>
                    <Avatar
                      className={styles.hostAvatar}
                      sx={{
                        width: 100,
                        height: 100,
                        flex: 1,
                      }}
                      variant='square'
                      src={avatarUrl}
                    >
                      <Box className={styles.avatarFallback}>
                        <UserIcon sx={{ color: 'var(--app-welcome-panel-icon-fg)', fontSize: '4.4rem' }} />
                      </Box>
                    </Avatar>

                    <Stack spacing={'10px'} justifyContent='flex-end' className={styles.detailsColumn}>
                      {!isDismissed &&
                        profile.isProviderInQueue !== undefined &&
                        (profile.unavailable || !profile.isProviderInQueue ? (
                          <Stack
                            direction='row'
                            spacing={0}
                            alignItems='center'
                            className={styles.detailsRowMobile}
                            sx={{ marginInlineStart: isRtl ? 'auto' : 0 }}
                          >
                            <Box sx={mobileStatusIconSlotSx}>
                              <CircleCrossIcon sx={{ color: 'var(--app-text-muted)', fontSize: '1.6rem' }} />
                            </Box>
                            <Typography
                              component='p'
                              variant='body2'
                              className={styles.detailsValue}
                              sx={{ textAlign: isRtl ? 'right' : 'left' }}
                            >
                              {t('patientWaitingRoom.unavailable')}
                            </Typography>
                          </Stack>
                        ) : (
                          <Stack
                            direction='row'
                            spacing={0}
                            alignItems='center'
                            className={styles.detailsRowMobile}
                            sx={{ marginInlineStart: isRtl ? 'auto' : 0 }}
                          >
                            <Box sx={mobileStatusIconSlotSx}>
                              <HeavyCheckIcon />
                            </Box>
                            <Typography
                              component='p'
                              variant='body2'
                              className={styles.detailsValue}
                              sx={{ textAlign: isRtl ? 'right' : 'left' }}
                            >
                              {t('patientWaitingRoom.available')}
                            </Typography>
                          </Stack>
                        ))}

                      {!isDismissed && (
                        <Button
                          variant='contained'
                          disabled={profile.unavailable || !profile.isProviderInQueue}
                          className={styles.messageBtn}
                          onClick={handleMessageClick}
                        >
                          {t('patientWaitingRoom.message')}
                        </Button>
                      )}
                    </Stack>
                  </Stack>

                  <Typography
                    component='p'
                    variant='h4'
                    sx={isRtl ? { textAlign: 'right', alignSelf: 'flex-end', width: '100%' } : undefined}
                  >
                    {profileFullName}
                  </Typography>

                  <Stack direction='row' spacing={0} alignItems='center' className={styles.detailsRow}>
                    <Box sx={detailsIconSlotSx}>
                      <HospitalIcon className={styles.hospitalIcon} />
                    </Box>
                    <Typography
                      component='p'
                      variant='body2'
                      className={classnames(styles.detailsValue, styles.detailsValueLatin)}
                    >
                      {profile.organization}
                    </Typography>
                  </Stack>

                  {profile.displayEmail && (
                    <Stack direction='row' spacing={0} alignItems='center' className={styles.detailsRow}>
                      <Box sx={detailsIconSlotSx}>
                        <EmailIcon className={styles.emailIcon} color='primary' />
                      </Box>
                      <Typography
                        component='p'
                        variant='body2'
                        className={classnames(styles.detailsValue, styles.detailsValueLatin)}
                      >
                        {profile.email}
                      </Typography>
                    </Stack>
                  )}

                  <Stack
                    direction='row'
                    alignItems='center'
                    flexWrap='wrap'
                    sx={{ gap: '8px', width: '100%', marginTop: '32px !important' }}
                  >
                    <Typography variant='body2' sx={{ fontSize: '1.2rem', whiteSpace: 'nowrap' }}>
                      {t('patientWaitingRoom.havingIssues')}
                    </Typography>
                    <Button
                      variant='outlined'
                      size='small'
                      sx={{ textTransform: 'none', flexShrink: 0 }}
                      startIcon={<NetworkCheckIcon sx={{ width: 20, height: 20 }} />}
                      onClick={() => setConnectionTestOpen(true)}
                    >
                      {t('patientWaitingRoom.testYourConnection')}
                    </Button>
                  </Stack>
                </Stack>
              </Hidden>
            </Stack>
            <Hidden mdUp smDown>
              <Box className={styles.preview}>
                {!isDismissed && (
                  <VideoPreviewWithControls
                    previewClassName={styles.videoPreview}
                    controlsClassName={styles.videoControls}
                  />
                )}
              </Box>
            </Hidden>
          </Stack>

          <Stack spacing='15px' alignItems='flex-start'>
            <Typography component='pre' variant='body1' whiteSpace='pre-wrap' className={classnames(styles.bio)}>
              {replaceURLsInString(profile.biography || '')}
            </Typography>
          </Stack>
        </CardContent>
      </Card>
    </>
  );
}
